import { FeatureProps } from '@portals/core/src/components/ProductComparison/Feature/Feature';
import { useProductComparisonContext } from '@portals/core/src/components/ProductComparison/ProductComparisonContext';
import { useSPKCookie } from '@portals/forms/src/hooks/useSPKCookie/useSPKCookie';
import axios from 'axios';
import React, { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type GirokontoData = {
  productNames: string[];
  ctas: string[];
  listings: Array<Array<{ id: number; content: string; note?: string; type: string; value: string }>>;
  priceListingLinks: Array<Array<{ id: number; label: string; uri: string }>>;
  priceListingNote: string[];
  services: Array<Array<{ id: number; content: string; headline: string; type: string; note?: string }>>;
  benefits: Array<Array<{ id: number; content: string; note?: string; type: string }>>;
  featureGroups: {
    [name: string]: FeatureProps[];
  };
};

type Data = GirokontoData | 'loading' | undefined;

export type GirokontoContextValue = {
  data: Data;

  doLoadData: () => void;
};

const GirokontoContext = React.createContext<GirokontoContextValue | null>(null);

export { GirokontoContext };

export const GirokontoContextProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { changeSelected } = useProductComparisonContext();
  const [data, setData] = useState<Data>(undefined);
  const [loadData, setLoadData] = useState<boolean>(false);
  const { bankCode } = useSPKCookie();

  const doLoadData = useCallback(() => {
    setLoadData(true);
  }, []);

  const value = useMemo(() => ({ data, doLoadData }), [doLoadData, data]);

  useEffect(() => {
    const load = async (bankCode: string) => {
      setData('loading');
      const abortTime = +process.env.TIMEOUT_FETCH || 5000;
      const abortController = new AbortController();
      const abortTimeout = setTimeout(() => {
        abortController.abort(`Timeout reached (${abortTime} ms)`);
      }, abortTime);
      const url = new URL(window.location.origin);
      url.searchParams.append('institute.bankCode', bankCode);
      url.pathname = '/api/products/girokonto';
      const { data } = await axios.get(url.toString(), { signal: abortController.signal });
      clearTimeout(abortTimeout);
      setData(data);
      changeSelected(data.defaultIndex);
    };

    if (bankCode && loadData) {
      load(bankCode).catch((e) => {
        console.error(e);
        setData(undefined);
      });
    }
  }, [bankCode, changeSelected, loadData]);

  return <GirokontoContext.Provider value={value}>{children}</GirokontoContext.Provider>;
};

export const useGirokontoContext = (): GirokontoContextValue => {
  const context = useContext(GirokontoContext);

  if (context === null) {
    throw new Error('useGirokontoContext must be used within a GirokontoContextProvider');
  }

  useEffect(() => {
    context.doLoadData();
  }, [context]);

  return context;
};
